import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "./chart";
import type { ChartConfig } from "./chart";
import { cn } from "../lib/utils";

const DEFAULT_COLORS = ["#655560", "#1564BF", "#405F90", "#8884d8", "#82ca9d", "#ffc658"];

export interface ChartBarProps {
  data: Array<Record<string, unknown>>;
  dataKey: string;
  categoryKey: string;
  additionalDataKeys?: string[];
  colors?: string[];
  className?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  height?: number;
  layout?: "horizontal" | "vertical";
}

export const ChartBar = ({
  data,
  dataKey,
  categoryKey,
  additionalDataKeys = [],
  colors = DEFAULT_COLORS,
  className,
  showLegend = false,
  showGrid = true,
  height = 300,
  layout = "horizontal",
}: ChartBarProps) => {
  const allDataKeys = useMemo(() => [dataKey, ...additionalDataKeys], [dataKey, additionalDataKeys]);

  const chartConfig = useMemo(() => {
    return allDataKeys.reduce<ChartConfig>((acc, key, index) => {
      acc[key] = {
        label: key.charAt(0).toUpperCase() + key.slice(1),
        color: colors[index % colors.length],
      };
      return acc;
    }, {});
  }, [allDataKeys, colors]);

  const isVertical = layout === "vertical";

  return (
    <ChartContainer config={chartConfig} className={cn("aspect-auto w-full", className)} style={{ height }}>
      <BarChart data={data} layout={layout}>
        {showGrid && <CartesianGrid vertical={!isVertical} horizontal={isVertical} />}
        {isVertical ? (
          <>
            <XAxis type="number" tickLine={false} axisLine={false} tickMargin={8} />
            <YAxis type="category" dataKey={categoryKey} tickLine={false} axisLine={false} tickMargin={8} width={80} />
          </>
        ) : (
          <>
            <XAxis
              dataKey={categoryKey}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) =>
                typeof value === "string" && value.length > 10 ? `${value.slice(0, 10)}...` : value
              }
            />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} />
          </>
        )}
        <ChartTooltip
          cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
          content={
            <ChartTooltipContent
              formatter={(value: number | string, name: string) => (
                <div className="flex items-center justify-between gap-4">
                  <span>{chartConfig[name]?.label ?? name}</span>
                  <span className="font-mono font-medium">
                    {typeof value === "number" ? value.toLocaleString() : value}
                  </span>
                </div>
              )}
            />
          }
        />
        {allDataKeys.map((key) => (
          <Bar key={key} dataKey={key} fill={`var(--color-${key})`} radius={[4, 4, 0, 0]} />
        ))}
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}
      </BarChart>
    </ChartContainer>
  );
};
